import { useEffect, useState } from 'react';
import { useUiStore } from '@/state/uiStore.js';

/**
 * Icon class per toast type — mirrors the monolith's `icons` map in
 * `showToast()` so visual parity is preserved.
 */
const TOAST_ICONS = {
  success: 'fa-check-circle',
  error: 'fa-times-circle',
  warning: 'fa-exclamation-triangle',
  info: 'fa-info-circle'
};

/**
 * Single toast row. Handles its own auto-dismiss timer and the 300ms
 * fade-out animation (the `.toast-hiding` class is already styled in
 * the extracted toasts.css).
 *
 * @param {{ toast: import('@/state/uiStore.js').ToastEntry }} props
 */
export default function Toast({ toast }) {
  const dismissToast = useUiStore((s) => s.dismissToast);
  const [hiding, setHiding] = useState(false);

  useEffect(() => {
    if (toast.duration <= 0) return undefined;
    const timer = setTimeout(() => beginHide(), toast.duration);
    return () => clearTimeout(timer);
    // toast.duration is constant per toast id; toast.id is stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toast.id]);

  function beginHide() {
    if (hiding) return;
    setHiding(true);
    // Match the monolith's 300ms removal delay.
    setTimeout(() => dismissToast(toast.id), 300);
  }

  const iconClass = TOAST_ICONS[toast.type] ?? TOAST_ICONS.info;

  return (
    <div
      className={`toast toast-${toast.type}${hiding ? ' toast-hiding' : ''}`}
      role={toast.type === 'error' ? 'alert' : 'status'}
      aria-live={toast.type === 'error' ? 'assertive' : 'polite'}
    >
      <i className={`fas ${iconClass} toast-icon`} aria-hidden="true" />
      <div className="toast-content">
        <div className="toast-title">{toast.title}</div>
        {toast.message && <div className="toast-message">{toast.message}</div>}
        {/* An action turns the toast into something you can act on rather than
            just read — an approval waiting on you is time-limited, and making
            the operator go and find it is how it gets missed. Dismisses itself
            on click so the toast does not linger over whatever it opened. */}
        {toast.actionLabel && toast.onAction && (
          <button
            type="button"
            className="toast-action"
            onClick={() => {
              beginHide();
              toast.onAction();
            }}
          >
            {toast.actionLabel} <i className="fas fa-arrow-right" aria-hidden="true" />
          </button>
        )}
      </div>
      <button
        className="toast-close"
        onClick={beginHide}
        aria-label="Dismiss notification"
        type="button"
      >
        &times;
      </button>
    </div>
  );
}
